import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import OpenAI from "openai";
import cardBg from "../images/memetag/card-bg.png";
import bgMain from "../images/bg-main.png";
import PromptEditor from "./PromptEditor";
import ShareButtons from "./common/ShareButtons";
import {
  getWalletData,
  fetchWalletSummary,
  formatTradeSummary,
} from "../services/walletService";

// Import new English tag images
import buyEarlySellEarlyImage from "../images/memetag/types_en/buy_early_sell_early.png";
import buyAndHoldImage from "../images/memetag/types_en/buy_and_hold.png";
import buyEarlyAndRoundTripImage from "../images/memetag/types_en/buy_early_and_round_trip.png";
import bottomSellerImage from "../images/memetag/types_en/bottom_seller.png";
import buyBigSellBigImage from "../images/memetag/types_en/buy_big_sell_big.png";
import buyHighSellLowImage from "../images/memetag/types_en/buy_high_sell_low.png";
import makeALotOfMoneyImage from "../images/memetag/types_en/make_a_lot_of_money.png";
import loseALotOfMoneyImage from "../images/memetag/types_en/lose_a_lot_of_money.png";

// Import card background image

const MemeCoinTagENDetail = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("form");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState("");
  const [parsedResult, setParsedResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressStatus, setProgressStatus] = useState("Analyzing...");
  const [streamComplete, setStreamComplete] = useState(false);
  const [controller, setController] = useState(null);
  const resultRef = useRef(null);
  const [currentPrompt, setCurrentPrompt] =
    useState(`Analyze the following transactions:

{{tradeSummary}}

If multiple trading patterns are present, randomly select ONE of the most suitable styles from:
- buy_early_sell_early: Quick profit taking on new tokens
- buy_and_hold: Long-term holding through volatility
- buy_early_and_round_trip: Early entry but missed profit taking
- bottom_seller: Frequent selling at local bottoms
- buy_big_sell_big: Notably large trade usd value
- buy_high_sell_low: FOMO buying and panic selling
- make_a_lot_of_money: Consistent successful patterns
- lose_a_lot_of_money: Poor timing and decisions

Determine:
1. Trading style (ONE random choice from above if multiple patterns match)
2. Token preference (based on MAJORITY): animal|ai|tiktok|general
3. Brief behavior description
4. Humorous short description (max 100 words): Include specific details like which coins were traded, loss percentages, and trading frequency - don't be too vague. Based on their most distinctive behavior, explain what the trading style means (e.g., P小将, P大将, 火车头), and include specific tickers with their price movements. The goal is to make it sound funny with web3 vibes that users would want to share on X. Important: Must use second person ("you") to address the user directly, not third person.

Please return the following information in JSON format:
{
  "tag": "", // Trading style (ONE of the styles listed above)
  "tokenPreference": "", // ONE of: animal, ai, tiktok, general
  "behavior": "", // Brief description of the user's trading behavior
  "humor": "" // Humorous short description (max 100 words): Include specific details like which coins were traded, loss percentages, and trading frequency - don't be too vague. Based on their most distinctive behavior, explain what the trading style means, and include specific tickers with their price movements. The goal is to make it sound funny with web3 vibes that users would want to share on X. Important: Must use second person ("you") to address the user directly, not third person.
}

Ensure the return is in valid JSON format with no other text.`);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  // 注释掉未使用的变量以解决 lint 警告
  // const [summaryData, setSummaryData] = useState(null);
  // const [tradeSummary, setTradeSummary] = useState("");
  const [promptReady, setPromptReady] = useState(false);
  const [walletAddress, setWalletAddress] = useState("");

  // Handle back to form
  const handleBackToForm = () => {
    if (parsedResult && step === "result") {
      // If on result page, first return to form state
      setParsedResult(null);
      setStep("form");
      setResult("");
      setProgress(0);
      setStreamComplete(false);
      if (controller) {
        controller.abort();
      }
    } else {
      // Otherwise return to home page
      navigate(-1);
    }
  };

  // Get wallet data
  const handleGetWalletData = useCallback(
    async (address) => {
      if (!address) return;

      try {
        setIsSummaryLoading(true);

        // Get wallet data
        const walletData = await getWalletData(address);

        if (!walletData) {
          console.log("No transaction records found, trying to refresh data");
          // If no data or transaction is empty, try refreshing data
          await fetchWalletSummary(address);
          const refreshedData = await getWalletData(address);
          // setSummaryData(refreshedData);

          // Format trade summary
          if (refreshedData) {
            const summary = formatTradeSummary(refreshedData);
            // setTradeSummary(summary);
            // Replace placeholder in prompt
            const updatedPrompt = currentPrompt.replace(
              "{{tradeSummary}}",
              summary
            );
            setCurrentPrompt(updatedPrompt);
            setPromptReady(true);
          } else {
            console.error("No trade data available after refresh");
          }
        } else {
          // If there is data, use it directly
          // setSummaryData(walletData);

          // Format trade summary
          const summary = formatTradeSummary(walletData);
          // setTradeSummary(summary);
          // Replace placeholder in prompt
          const updatedPrompt = currentPrompt.replace("{{tradeSummary}}", summary);
          setCurrentPrompt(updatedPrompt);
          setPromptReady(true);
        }
      } catch (error) {
        console.error("Error getting wallet data:", error);
      } finally {
        setIsSummaryLoading(false);
      }
    },
    [currentPrompt]
  );

  // Get wallet address and data
  useEffect(() => {
    const savedAddress = localStorage.getItem("bscAddress");
    if (savedAddress) {
      setWalletAddress(savedAddress);
      handleGetWalletData(savedAddress);
    }
  }, [handleGetWalletData]);

  // Handle form submission and API request
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setStep("result");

    // Create an abort controller
    const abortController = new AbortController();
    setController(abortController);

    // Create OpenAI client
    const openai = new OpenAI({
      baseURL: "https://api.deepseek.com",
      apiKey: "sk-326e542c536742f3bb5f4d8b019a614e",
      dangerouslyAllowBrowser: true,
    });

    // Initialize progress
    setProgress(0);
    setProgressStatus("Analyzing...");

    // Simulate progress update
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) {
          clearInterval(progressInterval);
          return prev;
        }
        const increment = Math.random() * 10;
        const newProgress = Math.min(prev + increment, 95);

        // Update progress message based on progress
        if (newProgress < 30) {
          setProgressStatus("Analyzing wallet transactions...");
        } else if (newProgress < 60) {
          setProgressStatus("Identifying transaction patterns...");
        } else if (newProgress < 90) {
          setProgressStatus("Generating memecoin trading tag...");
        }

        return newProgress;
      });
    }, 1000);

    try {
      // Get BSC address from localStorage if not in form
      const bscAddress = walletAddress || localStorage.getItem("bscAddress");

      if (!bscAddress) {
        throw new Error("BSC address is required");
      }

      // Build prompt - Use the prepared prompt
      const prompt = currentPrompt;

      // Send request to OpenAI (non-streaming)
      const response = await openai.chat.completions.create(
        {
          model: "deepseek-chat",
          messages: [{ role: "system", content: prompt }],
          stream: false,
        },
        { signal: abortController.signal }
      );

      // Get the complete response text
      const responseText = response.choices[0]?.message?.content || "";
      setResult(responseText);

      // Try to parse JSON
      try {
        // Handle possible Markdown code blocks
        let cleanedResponse = responseText;
        if (responseText.includes('```json')) {
          cleanedResponse = responseText.replace(/```json\n|```/g, '');
        }
        
        // Find JSON object start and end
        const jsonStart = cleanedResponse.indexOf("{");
        const jsonEnd = cleanedResponse.lastIndexOf("}");

        if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
          const jsonStr = cleanedResponse.substring(jsonStart, jsonEnd + 1);
          const parsed = JSON.parse(jsonStr);
          if (parsed && parsed.tag) {
            // If no title field, generate a title based on tag
            if (!parsed.title) {
              // Generate corresponding title based on tag
              const tagTitleMap = {
                buy_early_sell_early: "Profit is profit",
                buy_and_hold: "Diamond hand degen",
                buy_early_and_round_trip: "Roundtrip retard",
                bottom_seller: "Top signal",
                buy_big_sell_big: "Onchain terrorist",
                buy_high_sell_low: "Exit liquidity",
                make_a_lot_of_money: "Shitcoin GCR",
                lose_a_lot_of_money: "McDonald is always hiring",
              };
              parsed.title = tagTitleMap[parsed.tag] || `${parsed.tag}`;
            }
            
            // Set fixed text based on tag
            const tagBehaviorMap = {
              buy_early_sell_early:
                "Quickly buying and selling, making a profit from new tokens.",
              buy_and_hold:
                "Holding onto tokens for the long haul, riding out market volatility.",
              buy_early_and_round_trip:
                "Entering trades early, but missing out on profit taking opportunities.",
              bottom_seller:
                "Frequently selling at local bottoms, potentially missing out on future gains.",
              buy_big_sell_big:
                "Making large trades, with significant USD value.",
              buy_high_sell_low:
                "Buying high and selling low, potentially due to FOMO or panic selling.",
              make_a_lot_of_money:
                "Consistently making successful trades, with a strong track record.",
              lose_a_lot_of_money:
                "Struggling with poor timing and decision making, leading to losses.",
            };
            
            if (tagBehaviorMap[parsed.tag]) {
              parsed.behavior = tagBehaviorMap[parsed.tag];
            }
            
            setParsedResult(parsed);
          }
        }
      } catch (jsonError) {
        console.error("JSON parsing failed:", jsonError);
      }

      // Update progress to 100%
      setProgress(100);
      setProgressStatus("Analysis complete!");
      setStreamComplete(true);
    } catch (error) {
      console.error("Error:", error);
      setResult(`Error: ${error.message}`);
      setStreamComplete(true);
    } finally {
      // Clear progress bar timer
      clearInterval(progressInterval);
      setIsLoading(false);
    }
  };

  // Ensure result scrolling
  useEffect(() => {
    if (resultRef.current && result) {
      if (resultRef.current) {
        resultRef.current.scrollTop = resultRef.current.scrollHeight;
      }
    }
  }, [result, streamComplete]);

  // Cleanup resources on component unmount
  useEffect(() => {
    return () => {
      if (controller) {
        controller.abort();
      }
    };
  }, [controller]);

  // Handle prompt update from PromptEditor
  const handleSendPrompt = (newPrompt) => {
    setCurrentPrompt(newPrompt);
  };

  // Get the appropriate image based on tag
  const getTagImage = (tag) => {
    if (!tag) return cardBg;

    const tagMap = {
      "buy_early_sell_early": buyEarlySellEarlyImage,
      "buy_and_hold": buyAndHoldImage,
      "buy_early_and_round_trip": buyEarlyAndRoundTripImage,
      "bottom_seller": bottomSellerImage,
      "buy_big_sell_big": buyBigSellBigImage,
      "buy_high_sell_low": buyHighSellLowImage,
      "make_a_lot_of_money": makeALotOfMoneyImage,
      "lose_a_lot_of_money": loseALotOfMoneyImage
    };

    return tagMap[tag] || cardBg;
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* Background elements */}
      <div className="fixed inset-0 overflow-hidden">
        <div
          className="absolute w-full h-full overflow-hidden"
          style={{
            backgroundImage: `url(${bgMain})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.8,
          }}
        ></div>
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full max-w-md mx-auto pt-8 px-4 pb-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 relative">
          <button
            onClick={handleBackToForm}
            className="text-[#1c1f25] hover:text-[#f784ff] transition-colors z-10"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h2 className="text-[#1c1f25] text-xl font-medium absolute left-0 right-0 text-center">
            Memecoin Tag
          </h2>
        </div>

        <AnimatePresence mode="wait">
          {/* Main card with progress bar - Show when loading but no result yet */}
          {(step === "form" || (isLoading && !parsedResult)) && (
            <motion.div
              key="main-card"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              className="mt-6 w-[345px] mx-auto"
            >
              {/* Memecoin Tag Card - Based on Figma design */}
              <div className="flex flex-col w-[345px] items-start gap-[15px] p-[15px] relative bg-white rounded-[16px] overflow-hidden shadow-lg mx-auto">
                <div className="flex flex-col items-center gap-2.5 relative self-stretch w-full flex-[0_0_auto]">
                  {/* Card Header with background image */}
                  <img
                    className="relative self-stretch w-full h-[255px] object-cover"
                    src={cardBg}
                    alt="Card Background"
                  />

                  {/* Title */}
                  <p className="relative w-fit font-medium text-[#1c1f25] text-xl tracking-[0] leading-[35px] whitespace-nowrap">
                    Memecoin Trading Tag
                  </p>

                  {/* Subtitle */}
                  <p className="relative self-stretch font-normal text-[#676C77] text-base tracking-[0] leading-[23px]">
                    Discover your unique meme coin trading style — classified by
                    analyzing your trading history.
                  </p>

                  {/* Progress bar inside card */}
                  {isLoading && (
                    <div className="w-full mt-4 flex flex-col items-center">
                      <div className="flex flex-col w-full items-start gap-2.5">
                        {/* Progress bar */}
                        <div className="flex flex-col w-full items-start gap-2.5 relative flex-[0_0_auto] bg-[#cacfdb] rounded-[100px] border-[0.5px] border-solid border-[#E5E7EB]">
                          <div
                            className="relative h-[15px] rounded-[100px]"
                            style={{
                              width: `${progress}%`,
                              background:
                                "linear-gradient(72deg, rgba(112,235,122,1) 1%, rgba(95,217,200,1) 1%, rgba(112,168,235,1) 36%, rgba(116,166,236,1) 45%, rgba(179,142,255,1) 64%, rgba(227,86,197,1) 85%, rgba(249,173,88,1) 100%)",
                            }}
                          />
                        </div>

                        {/* Status text */}
                        <div className="relative self-stretch font-normal text-[#959AA3] text-sm text-center tracking-[0] leading-[23px]">
                          {Math.floor(progress)}%&nbsp;&nbsp;&nbsp;&nbsp;
                          {progressStatus}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Button below card - Only show in form step */}
              {step === "form" && (
                <motion.form
                  onSubmit={handleSubmit}
                  key="form-button"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4 }}
                  className="mt-6 w-[345px] mx-auto"
                >
                  <motion.button
                    type="submit"
                    className="w-full bg-gradient-to-r from-[#f784ff] to-[#b784ff] text-white py-3 rounded-lg font-medium hover:opacity-90 transition-all shadow-md flex items-center justify-center"
                    whileHover={{
                      scale: 1.05,
                      boxShadow: "0 10px 25px -5px rgba(247, 132, 255, 0.4)",
                    }}
                    whileTap={{ scale: 0.95 }}
                    disabled={isLoading || !promptReady || isSummaryLoading}
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Analyzing...
                      </div>
                    ) : isSummaryLoading ? (
                      <div className="flex items-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Loading wallet data...
                      </div>
                    ) : (
                      "Start Analysis"
                    )}
                  </motion.button>
                </motion.form>
              )}
            </motion.div>
          )}

          {/* Result card - Only show when there's a parsed result */}
          {parsedResult && (
            <motion.div
              key="result-content"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              className="mt-6 w-[355px] mx-auto"
            >
              {/* New Result Card Design */}
              <div
                id="meme-tag-card"
                className="relative w-[355px] min-h-[650px] bg-[#0a0e1f] rounded-[16px] overflow-hidden border border-solid border-transparent pb-6"
              >
                {/* Tag Image */}
                <motion.div className="w-full h-[345px] overflow-hidden">
                  <motion.img
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="w-full h-full object-cover"
                    src={getTagImage(parsedResult.tag)}
                    alt={parsedResult.tag}
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="px-7 flex flex-col space-y-5"
                >
                  {/* Title */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                    className="w-full mt-5 font-bold text-[22px] text-center tracking-[0] leading-[normal] text-[#f784ff]"
                  >
                    {parsedResult.title || parsedResult.tag}
                  </motion.div>

                  {/* Behavior Description */}
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.4 }}
                    className="w-full font-normal text-[#f5f5f5] text-base tracking-[0] leading-[normal]"
                  >
                    <span className="font-medium whitespace-pre-line">
                      {parsedResult.behavior}
                    </span>
                  </motion.div>

                  {/* Humor Comment */}
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.5 }}
                    className="w-full text-[#a0a0a0] text-sm italic tracking-[0] leading-[normal] border-l-2 border-[#3f51b5] pl-3 mt-2"
                  >
                    <span className="whitespace-pre-line">
                      {parsedResult.humor}
                    </span>
                  </motion.div>
                </motion.div>
              </div>

              {/* 底部按钮组 */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.6 }}
                className="mt-6"
              >
                <ShareButtons
                  cardId="meme-tag-card"
                  data={parsedResult}
                  featureName="Memecoin Trading Tag"
                  shareText={`See my Memecoin tag and then try yours: https://dapp.pinai.tech\n${parsedResult?.tag || ''} - ${parsedResult?.title || ''}${parsedResult?.humor ? `\n${parsedResult.humor}` : ''}`}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Prompt Editor - Only show when data is loaded, not analyzing, and in form step */}
        {promptReady && !isLoading && step === "form" && (
          <PromptEditor
            updatePrompt={currentPrompt}
            onSendPrompt={handleSendPrompt}
          />
        )}
      </div>
    </div>
  );
};

export default MemeCoinTagENDetail;
